import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import Dot from '../connect/Dot';
import LevelDisplay from '../connect/LevelDisplay';
import ResultsDisplay from '../connect/ResultsDisplay';
import { getGameDataProp } from '../Data';
import gameData from '../gameData';
import SoundAssetKeys from '../SoundAssetKeys';
import SelectScreen from './SelectScreen';

export default class Game extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  init() {
    this.puzzleCount = null;
    this.paths = null;

    this.drawContainer = null;
    this.drawingShape = null;
    this.dots = null;
    this.finalPicture = null;

    this.tmpSizerImage = null;
    this.dotSizePadding = 20;

    this.currentPuzzle = null;
    this.nextDotIndex = null;
    this.hintWrongCount = 0;
    this.puzzleWrongCount = 0;
    this.correctStreakCount = 0;
    this.correctCount = 0;

    this.btnNextPuzzle = null;

    this.drawSpaceWidth = null;
    this.drawSpaceHeight = null;
    this.curPicWidth = null;
    this.curPicHeight = null;

    this.isNextPuzzle = false;

    this.missArea = null;

    this.hintHand = null;

    this.resultsDisplay = null;
    this.showingResults = false;
    this.currentScore = null;

    this.soundStringPlayer = null; // Use to play sounds > 100
  }

  create() {
    this.currentPuzzle = 0;
    this.missArea = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0).setInteractive().setOrigin(0);
    this.missArea.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.clickMiss, this);

    this.puzzleCount = getGameDataProp(this, "puzzles").length;

    if (gameData.isAlphabet) {
      this.currentPuzzle = Phaser.Math.Between(0, 1);
      let c1 = getGameDataProp(this, "puzzles")[this.currentPuzzle]["bgColor"];
      this.cameras.main.setBackgroundColor(Phaser.Display.Color.HexStringToColor(c1).color);
    }
    this.finalImageKey = gameData.isAlphabet ? "alphabetpuzzleFinal" + (this.currentPuzzle + 1) : "level" + gameData.levelInfo.currentLevel + "puzzleFinal" + (this.currentPuzzle + 1);
    this.finalPicture = this.add.image(0, 0, this.finalImageKey).setOrigin(0).setVisible(false);

    this.drawContainer = this.add.container();
    this.drawingShape = this.add.graphics({ fillStyle: { color: 0xff0000 }, lineStyle: { color: 0xffffff, width: 2 } });
    this.drawingPath = new Phaser.Curves.Path();
    this.drawContainer.add(this.drawingShape);

    this.hintHand = ABCyaPhaser.Images.addImage(0, 0, "Hand_Pointer", this.drawContainer);
    this.hintHand.alpha = 0;

    this.btnNextPuzzle = ABCyaPhaser.Images.addImage(0, 0, "BtnNextPuzzle", this).setInteractive();
    this.btnNextPuzzle.x = this.scale.width - this.btnNextPuzzle.width;
    this.btnNextPuzzle.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.onNextPuzzleClick, this);
    this.btnNextPuzzle.alpha = 0;

    this.tmpSizerImage = this.add.image(0, 0, this.finalImageKey).setVisible(false);

    this.resultsDisplay = new ResultsDisplay(this);
    this.resultsDisplay.on("RESULTS_DISPLAYED", this.resultsFinished, this);
    this.resultsDisplay.alpha = 0;
    this.resultsDisplay.x = 860;
    if (gameData.isAlphabet) {
      this.resultsDisplay.x = 910;
    }
    this.resultsDisplay.y = 110;
    this.add.existing(this.resultsDisplay);

    this.atEnd = false;
    this.playedLevelStartSound = false;
    this.perfectLevel = true;

    this.btnMenu = ABCyaPhaser.Images.addImage(0, 0, "BtnMenu", this).setInteractive();
    this.btnMenu.x = this.scale.width - 40;
    this.btnMenu.y = this.scale.height - this.btnMenu.height - 15;
    this.btnMenu.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.onMenuClick, this);

    this.levelTitleDisplay = new LevelDisplay(this, gameData.levelInfo.currentLevel, "level" + gameData.levelInfo.currentLevel, getGameDataProp(this, "minNum"), getGameDataProp(this, "maxNum"), 80, 42, 18, 1);
    this.levelTitleDisplay.y = this.scale.height - 30;
    this.levelTitleDisplay.x = 25;
    this.add.existing(this.levelTitleDisplay);

    if (gameData.isAlphabet) {
      this.levelTitleDisplay.setVisible(false);
    }

    this.layoutRatio();
    this.loadPuzzle();
  }

  clickMiss() {
    this.hintWrongCount++;
    this.correctStreakCount = 0;
    if (this.hintWrongCount > 2) {
      this.showNextDotHint();
    }
  }

  showNextDotHint() {
    this.dots[this.nextDotIndex].pulse();
    let d = this.dots[this.nextDotIndex];
    this.hintHand.x = d.x - 12;
    this.hintHand.y = d.y - 12;
    this.hintHand.scale = 0.8;
    this.hintHand.alpha = 1;
    this.drawContainer.bringToTop(this.hintHand);
    this.tweens.killTweensOf(this.hintHand);
    this.tweens.timeline(
      {
        tweens:
          [
            ABCyaPhaser.Tweens.createTween(this.hintHand, 800, { scale: 0.4, completeDelay: 800 }, Phaser.Math.Easing.Quadratic.Out),
            ABCyaPhaser.Tweens.createTween(this.hintHand, 800, { scale: 0.8, completeDelay: 120 }, Phaser.Math.Easing.Quadratic.Out),
          ],
        loop: -1
      }
    );
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._pop_01b, this);
  }

  onNextPuzzleClick() {
    if (!this.showingResults) return; // Piggybacking this flag to prevent doubleclicks
    this.resultsDisplay.hide();
    this.showingResults = false;

    let newPicLoc = ABCyaPhaser.GameObjects.getScreenPosition(this.finalPicture);
    this.finalPicture.scale = this.drawContainer.scale;
    this.finalPicture.x = newPicLoc.x;
    this.finalPicture.y = newPicLoc.y;
    this.drawContainer.remove(this.finalPicture);
    this.tweens.killTweensOf(this.finalPicture);
    this.tweens.killTweensOf(this.btnNextPuzzle);
    ABCyaPhaser.Tweens.addTween(this.finalPicture, 240, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
    ABCyaPhaser.Tweens.addTween(this.btnNextPuzzle, 240, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);

    if (this.atEnd || gameData.isAlphabet) {
      this.scene.start(SelectScreen.KEY);
    }
    else {
      this.loadPuzzle();
    }
  }

  resultsFinished() {
    if (this.currentPuzzle < this.puzzleCount - 1) {
      if (this.currentScore < 100) {
        this.perfectLevel = false;
      }
      this.currentPuzzle++;
    }
    else {
      // store that user completed the level
      let li = gameData.levelInfo.completedLevels.indexOf(gameData.levelInfo.currentLevel);
      if (li < 0) {
        gameData.levelInfo.completedLevels.push(gameData.levelInfo.currentLevel);
      }

      // check the last puzzle too!
      if (this.currentScore < 100) {
        this.perfectLevel = false;
      }

      if (this.perfectLevel) {
        li = gameData.levelInfo.perfectLevels.indexOf(gameData.levelInfo.currentLevel);
        if (li < 0) {
          gameData.levelInfo.perfectLevels.push(gameData.levelInfo.currentLevel);
        }
      }
      this.perfectLevel = true;
      gameData.levelInfo.currentLevelIndex++;
      if (gameData.levelInfo.currentLevelIndex < gameData.levelInfo.selectedLevels.length) {
        this.currentPuzzle = 1;
        gameData.levelInfo.currentLevel = gameData.levelInfo.selectedLevels[gameData.levelInfo.currentLevelIndex];
        this.playedLevelStartSound = false;
      }
      else {
        this.atEnd = true;
      }
    }
    ABCyaPhaser.Tweens.addTween(this.btnNextPuzzle, 180, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);
  }

  onMenuClick() {
    this.scene.start(SelectScreen.KEY);
  }

  layoutRatio() {
    this.btnNextPuzzle.y = this.scale.height - 160;
    this.drawSpaceWidth = this.scale.width - this.btnNextPuzzle.width - 36;
    let heightBuffer = 20;
    this.dotSizePadding = 20;
    this.drawSpaceHeight = this.scale.height - heightBuffer;
    this.sizeAndPosition();
  }

  sizeAndPosition() {
    this.finalImageKey = gameData.isAlphabet ? "alphabetpuzzleFinal" + (this.currentPuzzle + 1) : "level" + gameData.levelInfo.currentLevel + "puzzleFinal" + (this.currentPuzzle + 1);
    this.tmpSizerImage.setTexture(this.finalImageKey);
    let hBuffer = (this.game.device.os.iPhone) ? this.dotSizePadding * 2 : this.dotSizePadding;
    let newScale = resizeDimensions(this.tmpSizerImage.width, this.tmpSizerImage.height, this.drawSpaceWidth - 50, this.drawSpaceHeight - hBuffer);
    this.drawContainer.x = this.drawSpaceWidth / 2 * newScale.scale - (newScale.width / 2 * newScale.scale);
    this.drawContainer.y = this.drawSpaceHeight / 2 * newScale.scale - (newScale.height / 2) * newScale.scale + this.dotSizePadding / 2;
    this.drawContainer.scale = newScale.scale;
  }

  loadPuzzle() {
    this.paths = getGameDataProp(this, "puzzles")[this.currentPuzzle]["linepaths"];
    let dotNumStart = getGameDataProp(this, "minNum");
    let dotNumEnd = getGameDataProp(this, "maxNum");

    let countBy = getGameDataProp(this, "puzzles")[this.currentPuzzle]["countby"];
    if (!countBy) {
      countBy = getGameDataProp(this, "countby");
      if (!countBy) countBy = 1; // Will default to 1
    }

    // Make our dots
    this.dots = [];
    let pointCount = this.paths.length;
    let fadeInDelay = 0;
    let fadeIncrement = 1500 / pointCount;
    for (let i = 0; i < pointCount; i++) {
      let item = new Dot(this, this, dotNumStart + i, null, null, null, null, countBy);
      let pathpoints = this.getPathPoints(this.paths[i]);
      let lastPos = pathpoints.length;
      item.x = pathpoints[lastPos - 2];
      item.y = pathpoints[lastPos - 1];
      item.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this.dotClick(item));
      this.dots.push(item);
      item.pulseIn(fadeInDelay);
      fadeInDelay += fadeIncrement;
    }

    for (let j = this.dots.length - 1; j >= 0; j--) {
      this.drawContainer.add(this.dots[j]);
    }

    this.sizeAndPosition();

    this.nextDotIndex = 1; // Always 1 ahead
    let color = getGameDataProp(this, "puzzles")[this.currentPuzzle]["bgColor"];
    this.cameras.main.setBackgroundColor(color);

    this.hintWrongCount = 0;
    this.puzzleWrongCount = 0;
    this.correctCount = 0;
    this.correctStreakCount = 0;

    this.time.delayedCall(fadeInDelay + 300, this.showNextDotHint, null, this);
    this.levelTitleDisplay.showLevelInfo(Phaser.Display.Color.HexStringToColor(color).color, 0xffffff, 2, dotNumStart, dotNumEnd);

    if (!gameData.isAlphabet && !this.playedLevelStartSound) {
      ABCyaPhaser.Audio.playOneShot("titles/level" + gameData.levelInfo.currentLevel, this);

      this.playedLevelStartSound = true;
    }
  }

  getPathPoints(str) {
    let instructions = [1, 2, 3, 4, 5];
    let paramCount = [2, 2, 4, 6, 0];
    let i = 0, l = str.length;
    let points = [];
    let x = 0, y = 0;
    let base64 = BASE_64;

    while (i < l) {
      let c = str.charAt(i);
      let n = base64[c];
      let fi = n >> 3; // highest order bits 1-3 code for operation.
      let f = instructions[fi];
      // check that we have a valid instruction & that the unused bits are empty:
      if (!f || (n & 3)) { throw ("bad path data (@" + i + "): " + c); }
      let pl = paramCount[fi];
      if (!fi) { x = y = 0; } // move operations reset the position.
      points.length = 0;
      i++;
      let charCount = (n >> 2 & 1) + 2;  // 4th header bit indicates number size for this operation.
      for (let p = 0; p < pl; p++) {
        let num = base64[str.charAt(i)];
        let sign = (num >> 5) ? -1 : 1;
        num = ((num & 31) << 6) | (base64[str.charAt(i + 1)]);
        if (charCount == 3) { num = (num << 6) | (base64[str.charAt(i + 2)]); }
        num = sign * num / 10;
        if (p % 2) { x = (num += x); }
        else { y = (num += y); }
        points[p] = num;
        i += charCount;
      }
    }
    return points;
  }

  dotClick(sender) {
    let index = this.dots.indexOf(sender);
    if (index == this.nextDotIndex) {
      this.dots[this.nextDotIndex].pulse();
      if (gameData.isAlphabet) {
        ABCyaPhaser.Audio.playOneShot("letters/" + sender.displayNum.toLowerCase() + "Letter", this);
      }
      else ABCyaPhaser.Audio.playOneShot("Sound" + sender.displayNum, this);
      this.drawLine(this.paths[this.nextDotIndex - 1]);
      this.nextDotIndex++;
      this.hintWrongCount = 0;
      this.correctCount++;
      this.correctStreakCount++;
      if (this.nextDotIndex >= this.paths.length) {
        this.drawStarterLine();
        this.time.delayedCall(330, this.finishedPuzzle, null, this);
      }
      else if (this.nextDotIndex < this.paths.length - 1) {
        this.drawContainer.add(this.dots[this.nextDotIndex]);
      }
      this.hideDotHint();
      // Check for halfway point
      if (Math.floor(this.dots.length / 2) === this.nextDotIndex) {
        let n = Phaser.Math.Between(1, 3);
        ABCyaPhaser.Audio.playOneShot("picIntro" + n, this);
      }
    }
    else {
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._incorrect, this);
      this.hintWrongCount++;
      this.puzzleWrongCount++;
      this.correctStreakCount = 0;
      if (this.hintWrongCount > 2) {
        this.showNextDotHint();
      }
    }
  }

  drawLine(tinycode) {
    decodePath(this.drawingPath, tinycode, this.drawingShape);
    this.drawingPath.draw(this.drawingShape);
  }

  drawStarterLine() {
    // Draw the last line to start
    this.drawLine(this.paths[this.paths.length - 1]);
  }

  finishedPuzzle() {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._slide_whistle_up_05, this);

    // Just in case we changed it when we tweened it out on next
    this.finalPicture.scale = 1.0;
    this.finalPicture.x = this.finalPicture.y = 0;
    this.drawContainer.add(this.finalPicture);// Put the final picture back into the draw container for display

    this.finalPicture.setTexture(this.finalImageKey);

    this.finalPicture.setVisible(true).alpha = 0;
    ABCyaPhaser.Tweens.addTween(this.finalPicture, 180, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out, () => this.playFinalImageSound());
    this.drawingShape.clear();
    this.drawingPath = new Phaser.Curves.Path();

    // Show results
    let total = this.dots.length;// Using the +1 so we get nice numbers when / 20
    let misses = this.puzzleWrongCount;
    let p = total - misses;
    if (p <= 0) {
      p = 0;
    }
    else {
      p = Math.round(p / total * 100);
    }
    this.currentScore = p;

    for (let i = 0; i < this.dots.length; i++) {
      this.dots[i].fadeAway();
    }
  }

  playFinalImageSound() {
    if (!gameData.isAlphabet) {
      let thesound = getGameDataProp(this, "puzzles")[this.currentPuzzle]["soundFile"];
      ABCyaPhaser.Audio.playOneShot("picture_names/" + thesound, this);
    }
    this.time.delayedCall(1000, this.displayResult, null, this);
  }

  displayResult() {
    this.resultsDisplay.showResults(this.currentScore);
    this.showingResults = true;
  }

  killDot(item) {
    this.drawContainer.remove(item, true);
    let index = this.dots.indexOf(item);
    this.dots.splice(index, 1);
  }

  hideDotHint() {
    this.tweens.killTweensOf(this.hintHand);
    ABCyaPhaser.Tweens.addTween(this.hintHand, 300, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
  }

  update(time, delta) {
    if (this.showingResults) {
      this.resultsDisplay.tick(delta);
    }
  }
}

function resizeDimensions(currentWidth, currentHeight, maxWidth, maxHeight) {
  let scale = 1;
  if (currentWidth > maxWidth || currentHeight > maxHeight) {
    scale = Math.min(maxWidth / currentWidth, maxHeight / currentHeight);
  }
  return { width: currentWidth * scale, height: currentHeight * scale, scale: scale };
}

// from CreateJs.Graphics.BASE_64 https://www.createjs.com/docs/easeljs/files/easeljs_display_Graphics.js.html#l323
const BASE_64 = { "A": 0, "B": 1, "C": 2, "D": 3, "E": 4, "F": 5, "G": 6, "H": 7, "I": 8, "J": 9, "K": 10, "L": 11, "M": 12, "N": 13, "O": 14, "P": 15, "Q": 16, "R": 17, "S": 18, "T": 19, "U": 20, "V": 21, "W": 22, "X": 23, "Y": 24, "Z": 25, "a": 26, "b": 27, "c": 28, "d": 29, "e": 30, "f": 31, "g": 32, "h": 33, "i": 34, "j": 35, "k": 36, "l": 37, "m": 38, "n": 39, "o": 40, "p": 41, "q": 42, "r": 43, "s": 44, "t": 45, "u": 46, "v": 47, "w": 48, "x": 49, "y": 50, "z": 51, "0": 52, "1": 53, "2": 54, "3": 55, "4": 56, "5": 57, "6": 58, "7": 59, "8": 60, "9": 61, "+": 62, "/": 63 };

/**
 * Modified decodePath function from CreateJs.Graphics https://www.createjs.com/docs/easeljs/files/easeljs_display_Graphics.js.html#l1011
 * @param {Phaser.Curves.Path} path 
 * @param {*} str 
 * @param {Phaser.GameObjects.Graphics} graphics
 */
function decodePath(path, str, graphics) {
  let instructions = [path.moveTo, path.lineTo, path.quadraticBezierTo, path.cubicBezierTo, path.closePath];
  let paramCount = [2, 2, 4, 6, 0];
  let i = 0, l = str.length;
  let params = [];
  let x = 0, y = 0;
  let base64 = BASE_64;

  while (i < l) {
    let c = str.charAt(i);
    let n = base64[c];
    let fi = n >> 3; // highest order bits 1-3 code for operation.
    let f = instructions[fi];
    // check that we have a valid instruction & that the unused bits are empty:
    if (!f || (n & 3)) { throw ("bad path data (@" + i + "): " + c); }
    let pl = paramCount[fi];
    if (!fi) { x = y = 0; } // move operations reset the position.
    params.length = 0;
    i++;
    let charCount = (n >> 2 & 1) + 2;  // 4th header bit indicates number size for this operation.
    for (let p = 0; p < pl; p++) {
      let num = base64[str.charAt(i)];
      let sign = (num >> 5) ? -1 : 1;
      num = ((num & 31) << 6) | (base64[str.charAt(i + 1)]);
      if (charCount == 3) { num = (num << 6) | (base64[str.charAt(i + 2)]); }
      num = sign * num / 10;
      if (p % 2) { x = (num += x); }
      else { y = (num += y); }
      params[p] = num;
      i += charCount;
    }
    if (fi == 2) {
      // the params for path.quadraticBezierTo are different than what the original CreateJs bezierTo function was, fix it
      let endY = Phaser.Utils.Array.RemoveAt(params, 3);
      let endX = Phaser.Utils.Array.RemoveAt(params, 2);
      params.unshift(endX, endY);
    }
    if (fi == 3) {
      // the params for path.cubicBezierTo are different than what the original CreateJs bezierTo function was, fix it
      let endY = Phaser.Utils.Array.RemoveAt(params, 5);
      let endX = Phaser.Utils.Array.RemoveAt(params, 4);
      params.unshift(endX, endY);
    }
    f.apply(path, params);
  }
}